/**
 * First-install progress (DISCUSSION-sinkron, spec 9.7 step 6) as the worker reports it:
 * one `snapshot:progress` event per page. Kept in a small store from the moment the
 * session starts, because the snapshot begins right after login while the PIN is being
 * created, before the Unduhan pertama screen mounts. Pure reducer, tested under Node.
 */
import { syncedTables, uiStrings as ui } from '@satu/shared'
import { create } from 'zustand'
import type { SnapshotProgress } from '../db/protocol'

export interface Kemajuan {
  /** Rows the server holds per table; filled by `mulai`, and per table by `tabel` events. */
  total: Record<string, number>
  /** Rows received per table so far. */
  diterima: Record<string, number>
  /** Epoch ms of the first event of this snapshot; null before one. */
  mulai: number | null
  /** Epoch ms of the latest event, for the rate. */
  diperbarui: number
  berjalan: boolean
  selesai: boolean
  gagal: boolean
}

export const KEMAJUAN_AWAL: Kemajuan = {
  total: {},
  diterima: {},
  mulai: null,
  diperbarui: 0,
  berjalan: false,
  selesai: false,
  gagal: false,
}

export function terapkan(prev: Kemajuan, p: SnapshotProgress, now: number): Kemajuan {
  switch (p.phase) {
    case 'mulai':
      return {
        total: { ...(p.counts ?? {}) },
        diterima: {},
        mulai: now,
        diperbarui: now,
        berjalan: true,
        selesai: false,
        gagal: false,
      }
    case 'tabel': {
      const total = { ...prev.total }
      const diterima = { ...prev.diterima }
      if (p.table !== undefined) {
        diterima[p.table] = p.received ?? 0
        if (p.total !== null && p.total !== undefined) total[p.table] = p.total
      }
      return {
        ...prev,
        total,
        diterima,
        mulai: prev.mulai ?? now,
        diperbarui: now,
        berjalan: true,
        selesai: false,
        gagal: false,
      }
    }
    case 'selesai':
      return { ...prev, diperbarui: now, berjalan: false, selesai: true, gagal: false }
    case 'gagal':
      return { ...prev, diperbarui: now, berjalan: false, gagal: true }
  }
}

export interface BarisKemajuan {
  id: string
  label: string
  done: number
  total: number | null
}

const LABEL: Record<string, string> = ui.masuk.unduhanPertama.tabel

/** Tables in snapshot order, only those with rows on the server or already received. */
export function barisKemajuan(k: Kemajuan): BarisKemajuan[] {
  return syncedTables
    .map((t) => t.name)
    .filter((name) => (k.total[name] ?? 0) > 0 || (k.diterima[name] ?? 0) > 0)
    .map((name) => ({
      id: name,
      label: LABEL[name] ?? name,
      done: k.diterima[name] ?? 0,
      total: k.total[name] ?? null,
    }))
}

function jumlah(map: Record<string, number>): number {
  let n = 0
  for (const v of Object.values(map)) n += v
  return n
}

export interface Ringkasan {
  selesai: number
  total: number
  /** Seconds left at the observed rate; null until a rate exists or when done. */
  sisaDetik: number | null
}

export function ringkasan(k: Kemajuan): Ringkasan {
  const total = jumlah(k.total)
  const selesai = Math.min(total, jumlah(k.diterima))
  let sisaDetik: number | null = null
  if (k.mulai !== null && selesai > 0 && total > selesai) {
    const elapsed = (k.diperbarui - k.mulai) / 1000
    if (elapsed > 0) sisaDetik = (total - selesai) / (selesai / elapsed)
  }
  return { selesai, total, sisaDetik }
}

interface UnduhanState {
  kemajuan: Kemajuan
  terima(p: SnapshotProgress, now?: number): void
}

export const useUnduhan = create<UnduhanState>((set) => ({
  kemajuan: KEMAJUAN_AWAL,
  terima(p, now = Date.now()) {
    set((state) => ({ kemajuan: terapkan(state.kemajuan, p, now) }))
  },
}))
